import {
    CheckCircleIcon,
    ArrowLeftIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    PhotoIcon,
    UserGroupIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { Avatar, Button } from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import {
    createChatRoom,
    getAllProfiles,
    updateChatRoom,
    uploadMediaFile,
} from "../../services/api";
import { ProfileId, ProfileResponse } from "../../types/types";

interface AddOrEditGroupProps {
    isOpen: boolean;
    onClose: () => void;
    group?: any;
    onSave?: (groupData: any) => void;
    onViewProfile?: (profileId?: string | number | null) => void;
}

export const AddOrEditGroup: React.FC<AddOrEditGroupProps> = ({
    isOpen,
    onClose,
    group,
    onSave,
    onViewProfile,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [groupName, setGroupName] = useState("");
    const [groupBio, setGroupBio] = useState("");
    const [selectedMemberIds, setSelectedMemberIds] = useState<ProfileId[]>([]);
    const [groupImagePreview, setGroupImagePreview] = useState<string>("");
    const [groupImageFile, setGroupImageFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [contacts, setContacts] = useState<ProfileResponse[]>([]);
    const [isFetchingContacts, setIsFetchingContacts] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [groupNameError, setGroupNameError] = useState<string>("");
    const [groupBioError, setGroupBioError] = useState<string>("");
    const [imageError, setImageError] = useState<string>("");

    const { t, i18n } = useTranslation('addOrEditGroup');
    const MAX_GROUP_NAME_LENGTH = 25;
    const MAX_GROUP_BIO_LENGTH = 125;
    const MAX_IMAGE_SIZE = 1 * 1024 * 1024;
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

    useEffect(() => {
        const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
        i18n.changeLanguage(savedLanguage);
    }, [i18n]);

    useEffect(() => {
        const fetchContacts = async () => {
            setIsFetchingContacts(true);
            setError(null);
            try {
                const profiles = await getAllProfiles(searchTerm);
                setContacts(profiles);
            } catch (err: any) {
                setError(t("errorLoadingContacts") || err?.response?.data?.message);
                setContacts([]);
            } finally {
                setIsFetchingContacts(false);
            }
        };

        if (isOpen) {
            fetchContacts();
        }
    }, [isOpen, searchTerm, t]);

    useEffect(() => {
        if (group) {
            setGroupName(group.name || "");
            setGroupBio(group.bio || "");
            setGroupImagePreview(group.profileImage || "");
            setSelectedMemberIds(group.memberIds || []);
        } else {
            setGroupName("");
            setGroupBio("");
            setGroupImagePreview("");
            setGroupImageFile(null);
            setSelectedMemberIds([]);
        }
        setSearchTerm("");
        setGroupNameError("");
        setGroupBioError("");
        setImageError("");
    }, [group, isOpen]);

    const validateGroupName = (name: string) => {
        if (!name.trim()) {
            setGroupNameError(t("groupNameRequired") || "Group name is required");
            return false;
        }
        if (name.length > MAX_GROUP_NAME_LENGTH) {
            setGroupNameError(t("groupNameTooLong") || `Group name must be ${MAX_GROUP_NAME_LENGTH} characters or less`);
            return false;
        }
        setGroupNameError("");
        return true;
    };

    const validateGroupBio = (bio: string) => {
        if (bio.length > MAX_GROUP_BIO_LENGTH) {
            setGroupBioError(t("groupBioTooLong") || `Description must be ${MAX_GROUP_BIO_LENGTH} characters or less`);
            return false;
        }
        setGroupBioError("");
        return true;
    };

    const validateImageFile = (file: File) => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setImageError(t("invalidImageType") || "Only JPG and PNG images are allowed");
            return false;
        }

        if (file.size > MAX_IMAGE_SIZE) {
            setImageError(t("imageTooLarge") || "Image size must be 1MB or less");
            return false;
        }

        setImageError("");
        return true;
    };

    const filteredContacts = contacts.filter((contact) => {
        const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.toLowerCase();
        const email = (contact.email || "").toLowerCase();
        const search = searchTerm.toLowerCase();

        return fullName.includes(search) || email.includes(search);
    });

    const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setGroupName(value);
        validateGroupName(value);
    };

    const handleGroupBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setGroupBio(value);
        validateGroupBio(value);
    };

    const handleGroupImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!validateImageFile(file)) {
            e.target.value = '';
            return;
        }

        setGroupImageFile(file);
        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result as string;
            setGroupImagePreview(result);
        };
        reader.readAsDataURL(file);
    };

    const handleCreateGroup = async () => {
        const isNameValid = validateGroupName(groupName);
        const isBioValid = validateGroupBio(groupBio);

        if (!isNameValid || !isBioValid) {
            return;
        }

        setIsLoading(true);
        try {
            let profileImageUrl = group?.profileImage || "";

            if (groupImageFile) {
                try {
                    const uploadResponse = await uploadMediaFile(groupImageFile);
                    profileImageUrl = uploadResponse || "";
                } catch (uploadError: any) {
                    toast.error(uploadError?.response?.data?.message || "Failed to upload image.");
                    setIsLoading(false);
                    return;
                }
            }

            const groupData = {
                name: groupName.trim(),
                bio: groupBio.trim(),
                memberIds: selectedMemberIds,
                profileImage: profileImageUrl,
            };

            if (group?.id) {
                await updateChatRoom(group.id, groupData);
                toast.success(t('groupUpdatedSuccess', 'Group updated successfully'));
            } else {
                await createChatRoom(groupData);
                toast.success(t('groupCreatedSuccess', 'Group created successfully'));
            }

            if (onSave) onSave(groupData);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setSearchTerm("");
        setGroupName("");
        setGroupBio("");
        setSelectedMemberIds([]);
        setGroupImagePreview("");
        setGroupImageFile(null);
        setContacts([]);
        setGroupNameError("");
        setGroupBioError("");
        setImageError("");

        onClose();
    };

    const handleRemoveImage = () => {
        setGroupImagePreview("");
        setGroupImageFile(null);
        setImageError("");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="w-full sm:w-[640px] sm:max-w-[640px] h-full sm:h-auto bg-white sm:rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden max-h-full sm:max-h-[90vh]">
                <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 shrink-0">
                    <button
                        type="button"
                        onClick={handleCloseModal}
                        disabled={isLoading}
                        className="sm:hidden size-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                    </button>
                    <h2 className="flex-1 text-base font-semibold font-['Plus_Jakarta_Sans'] text-gray-900 leading-6">
                        {group ? t("editGroup", "Edit group") : t("createNewGroup", "Create new group")}
                    </h2>
                    <button
                        onClick={handleCloseModal}
                        className="hidden sm:flex w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shrink-0"
                        title={t("close", "Close")}
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                        <div className="w-44 flex flex-col items-center gap-3 shrink-0">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                    {groupImagePreview ? (
                                        <img
                                            src={groupImagePreview}
                                            alt={t("groupPreviewAlt", "Group preview")}
                                            className="w-20 h-20 rounded-full object-cover"
                                        />
                                    ) : (
                                        <UserGroupIcon className="w-10 h-10 text-gray-400" />
                                    )}
                                </div>
                                <label className="w-8 h-8 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center cursor-pointer absolute right-0 bottom-0 shadow transition-colors">
                                    <PhotoIcon className="w-4 h-4 text-white" />
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png"
                                        onChange={handleGroupImageUpload}
                                        className="hidden"
                                    />
                                </label>
                                {groupImagePreview && (
                                    <button
                                        onClick={handleRemoveImage}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 shadow"
                                        title={t("removeImage", "Remove image")}
                                    >
                                        <XMarkIcon className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-800 leading-5">
                                    {t("uploadGroupPhoto", "Upload group photo")}
                                </p>
                                <p className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-400 leading-4 mt-0.5">
                                    JPG, PNG (max 1MB)
                                </p>
                                {imageError && (
                                    <div className="flex items-center justify-center gap-1 mt-1 text-red-500">
                                        <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                                        <span className="text-xs">{imageError}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 w-full space-y-3">
                            <div>
                                <div className="flex items-center gap-1 mb-1">
                                    <label className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-700 leading-5">
                                        {t("groupNameLabel", "Group name")}
                                    </label>
                                    <span className="text-red-500 text-sm font-medium">*</span>
                                </div>
                                <input
                                    type="text"
                                    placeholder={t("groupNamePlaceholder", "Enter group name")}
                                    className={`w-full h-9 px-3 py-2 bg-white rounded-xl outline outline-1 outline-offset-[-1px] text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                                        groupNameError
                                            ? 'outline-red-400 focus:outline-red-500'
                                            : 'outline-gray-200 focus:outline-blue-500'
                                    }`}
                                    value={groupName}
                                    onChange={handleGroupNameChange}
                                    maxLength={MAX_GROUP_NAME_LENGTH}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {groupNameError ? (
                                        <span className="text-xs text-red-500">{groupNameError}</span>
                                    ) : <span />}
                                    <span className="text-xs text-gray-400 font-['Plus_Jakarta_Sans']">
                                        {groupName.length}/{MAX_GROUP_NAME_LENGTH}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-1 mb-1">
                                    <label className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-700 leading-5">
                                        {t("groupDescriptionLabel", "Group description")}
                                    </label>
                                </div>
                                <textarea
                                    placeholder={t("groupDescriptionPlaceholder", "Enter group description")}
                                    className={`w-full h-20 px-3 py-2 bg-white rounded-xl outline outline-1 outline-offset-[-1px] text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 resize-none transition-all ${
                                        groupBioError
                                            ? 'outline-red-400 focus:outline-red-500'
                                            : 'outline-gray-200 focus:outline-blue-500'
                                    }`}
                                    value={groupBio}
                                    onChange={handleGroupBioChange}
                                    maxLength={MAX_GROUP_BIO_LENGTH}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {groupBioError ? (
                                        <span className="text-xs text-red-500">{groupBioError}</span>
                                    ) : <span />}
                                    <span className="text-xs text-gray-400 font-['Plus_Jakarta_Sans']">
                                        Characters: {groupBio.length}/{MAX_GROUP_BIO_LENGTH}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-100 my-2" />

                    {!group?.id && (
                        <div>
                            <div className="flex items-center gap-1 mb-1.5">
                                <label className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-800 leading-5">
                                    {t("addMembersLabel", "Add members")}
                                </label>
                                <span className="text-red-500 text-sm font-medium">*</span>
                            </div>

                            <div className="mb-3 relative">
                                <input
                                    type="text"
                                    placeholder={t("searchMembersPlaceholder", "Search members")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full h-9 pl-9 pr-3 py-2 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 focus:outline-blue-500 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5"
                                />
                                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5 pointer-events-none" />
                            </div>

                            <p className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 leading-5 mb-2">
                                {t("recent", "Recent")}
                            </p>

                            <div className="max-h-52 overflow-y-auto space-y-1 no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                {isFetchingContacts ? (
                                    <div className="flex items-center justify-center h-24 text-gray-500 text-sm font-['Plus_Jakarta_Sans']">
                                        {t("loadingContacts", "Loading contacts...")}
                                    </div>
                                ) : error ? (
                                    <div className="flex items-center justify-center h-24 text-red-500 text-sm font-['Plus_Jakarta_Sans']">
                                        {error}
                                    </div>
                                ) : filteredContacts.length === 0 ? (
                                    <div className="flex items-center justify-center h-24 text-gray-500 text-sm font-['Plus_Jakarta_Sans']">
                                        {searchTerm ? t("noContactsFound", "No contacts found") : t("noContactsAvailable", "No contacts available")}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        {filteredContacts.map((contact) => {
                                            const profileId = contact.id;
                                            if (!contact || profileId == null) return null;

                                            const isSelected = selectedMemberIds.includes(profileId);

                                            return (
                                                <div
                                                    key={profileId}
                                                    onClick={() => {
                                                        setSelectedMemberIds((prev) =>
                                                            isSelected
                                                                ? prev.filter((id) => id !== profileId)
                                                                : [...prev, profileId]
                                                        );
                                                    }}
                                                    className={`flex items-center gap-3 p-2.5 rounded-[20px] cursor-pointer transition-colors ${
                                                        isSelected
                                                            ? "bg-blue-50"
                                                            : "hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onViewProfile?.(contact.id);
                                                        }}
                                                        className="rounded-full outline-none focus:outline-none focus:ring-0 shrink-0"
                                                    >
                                                        <Avatar
                                                            src={contact.profileImage}
                                                            name={`${contact.firstName} ${contact.lastName}`}
                                                            className="w-9 h-9 text-xs"
                                                        />
                                                    </button>
                                                    <div className="flex-1 min-w-0 text-left">
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                onViewProfile?.(contact.id);
                                                            }}
                                                            className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 truncate hover:text-blue-600 outline-none focus:outline-none focus:ring-0 leading-5 block text-start"
                                                        >
                                                            {contact.firstName} {contact.lastName}
                                                        </button>
                                                        <p className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-400 truncate leading-4">
                                                            {contact.email}
                                                        </p>
                                                    </div>
                                                    {isSelected ? (
                                                        <CheckCircleIcon className="w-5 h-5 text-blue-600 shrink-0" />
                                                    ) : (
                                                        <div className="w-4 h-4 border border-gray-300 rounded-full shrink-0" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 shrink-0">
                    <div className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-500">
                        {selectedMemberIds.length > 0 && (
                            <span>
                                {selectedMemberIds.length} {selectedMemberIds.length === 1 ? t('member', 'member') : t('member_plural', 'members')} {t('selected', 'selected')}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="hidden sm:flex h-9 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-colors"
                            disabled={isLoading}
                        >
                            {t("cancel", "Cancel")}
                        </button>
                        <Button
                            onClick={handleCreateGroup}
                            disabled={
                                !groupName.trim() ||
                                groupNameError !== "" ||
                                groupBioError !== "" ||
                                (!group?.id && selectedMemberIds.length === 0) ||
                                isLoading
                            }
                            className="flex-1 sm:flex-none h-9 px-5 rounded-3xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                            isLoading={isLoading}
                        >
                            {group ? t("updateGroup", "Update group") : t("createGroup", "Create group")}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

